import type { ReactNode } from 'react';
import { useState } from 'react';

import { PreviewChange } from '@/components/settings/preview-change';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@/ux-common/components/tooltip/tooltip';

import type { PreviewChangeData } from './setting-preview.types';

export interface DirtyChangeTooltipProps {
  children: ReactNode;
  /** When set, hover shows PreviewChange (new <- old). Takes priority. */
  changePreview?: PreviewChangeData | null;
  /** Fallback tooltip when there is no change (e.g. bulk-edit disabled cell). */
  tooltipContent?: ReactNode;
}

/**
 * Conditionally wraps children in a hover tooltip showing a dirty-field change
 * preview (new <- old) or a plain fallback message. Pure presentational — consumers
 * supply pre-formatted strings and own their outer cell layout.
 *
 * The open state is mirrored only to lazy-mount the content on first hover, so
 * per-cell Radix overhead stays off-path when many dirty cells are visible. The
 * state itself is driven entirely by Radix. A change preview opens instantly
 * (`delayDuration={0}`): only a cell the user just edited carries one, so the
 * table island's transit dwell has nothing to gate. The fallback message keeps
 * the nearest provider's timing — a disabled column shows it on every cell,
 * which is exactly the spray the island dwell exists for (UX-3107).
 */
export const DirtyChangeTooltip = ({
  children,
  changePreview,
  tooltipContent,
}: DirtyChangeTooltipProps): ReactNode => {
  const [open, setOpen] = useState(false);

  const tooltipBody = changePreview ? (
    <PreviewChange
      displayCurrent={changePreview.displayCurrent}
      displayOriginal={changePreview.displayOriginal}
    />
  ) : (
    tooltipContent
  );

  if (!tooltipBody) {
    return children;
  }

  return (
    <Tooltip delayDuration={changePreview ? 0 : undefined} open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <div className="w-full" data-component="DirtyChangeTooltip">
          {children}
        </div>
      </TooltipTrigger>
      {open ? (
        <TooltipPortal>
          <TooltipContent>
            {tooltipBody}
            <TooltipArrow />
          </TooltipContent>
        </TooltipPortal>
      ) : null}
    </Tooltip>
  );
};
